import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, isConflict, notifyStockChanged } from "@/lib/api";
import { ConflictDialog } from "@/components/ConflictDialog";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

const productsChanged = () => {
  window.dispatchEvent(new Event("products:changed"));
  notifyStockChanged();
};

export const DeleteProductDialog = ({ product, onClose }) => {
  const confirm = async () => {
    try {
      await api.delete(`/products/${product.id}`);
      toast.success(`"${product.name}" removed — all reports updated`);
      productsChanged();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AlertDialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="delete-product-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove this product?</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block font-medium text-foreground">{product?.sku} · {product?.name}</span>
            <span className="mt-2 block">It will disappear from the dashboard, product master, daily entry, ledger, reorder plan, sales analysis, dead stock and exports. Its past entries are kept, and you can restore it any time from "Removed products".</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-product-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} className="bg-red-600 hover:bg-red-700" data-testid="delete-product-confirm">Remove product</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const RemovedProductsDialog = ({ open, onOpenChange }) => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/products/removed").then((r) => setRows(r.data));
  useEffect(() => { if (open) load(); }, [open]);
  const restore = async (p) => {
    try {
      await api.post(`/products/${p.id}/restore`);
      toast.success(`"${p.name}" restored`);
      productsChanged();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="removed-products-dialog">
        <DialogHeader><DialogTitle>Removed products</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
          {rows.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 py-2" data-testid={`removed-row-${p.sku}`}>
              <div className="min-w-0"><div className="truncate text-sm">{p.name}</div><div className="font-mono text-[10px] text-muted-foreground">{p.sku} · {p.category}</div></div>
              <Button size="sm" variant="outline" onClick={() => restore(p)} data-testid={`restore-btn-${p.sku}`}>Restore</Button>
            </div>
          ))}
          {!rows.length && <div className="py-8 text-center text-sm text-muted-foreground">No removed products.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const productForm = (p, categories) => (p ? { name: p.name, category: p.category, price: p.price, sku: p.sku,
  opening_a: p.opening_a, opening_b: p.opening_b, weight_kg: p.weight_kg ?? "" } : { name: "", category: categories[0] || "", price: 0, sku: "", opening_a: 0, opening_b: 0, weight_kg: "" });

export const ProductDialog = ({ open, onOpenChange, product, categories, onCreated }) => {
  const [f, setF] = useState({});
  const [rev, setRev] = useState(0);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setF(productForm(product, categories)); setRev(product?.rev || 0); }
  }, [open, product, categories]);
  const reload = async () => {
    try {
      const { data } = await api.get(`/products/${product.id}`);
      setF(productForm({ ...data, opening_a: data.opening?.A ?? 0, opening_b: data.opening?.B ?? 0 }, categories));
      setRev(data.rev || 0);
      productsChanged();
      toast.success("Latest version loaded");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    const body = { ...f, price: Number(f.price) || 0, opening_a: Number(f.opening_a) || 0, opening_b: Number(f.opening_b) || 0, sku: f.sku || null,
      weight_kg: f.weight_kg === "" || f.weight_kg == null ? null : Number(f.weight_kg), expected_rev: rev };
    setBusy(true);
    try {
      if (product) await api.put(`/products/${product.id}`, body);
      else { const { data } = await api.post("/products", body); onCreated?.(data); }
      toast.success(product ? "Product updated — all reports updated" : "Product added — all reports updated");
      productsChanged();
      onOpenChange(false);
    } catch (err) { if (isConflict(err)) setConflict(true); else toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{product ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Field label="Product name"><Input required value={f.name || ""} onChange={set("name")} data-testid="product-name-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Input list="cat-list" required value={f.category || ""} onChange={set("category")} placeholder="Pick or type a new one" data-testid="product-category-input" />
              <datalist id="cat-list">{[...new Set((categories || []).map((c) => (c ?? "").toString().trim()).filter(Boolean))].map((c) => <option key={c} value={c} />)}</datalist>
            </Field>
            <Field label="SKU (auto if empty)"><Input value={f.sku || ""} onChange={set("sku")} data-testid="product-sku-input" /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Price (₹)"><Input type="number" step="1" value={f.price} onChange={set("price")} data-testid="product-price-input" /></Field>
            <Field label="Weight (kg/unit)"><Input type="number" step="any" min="0" value={f.weight_kg ?? ""} onChange={set("weight_kg")} data-testid="product-weight-input" /></Field>
            <Field label="Opening WH A"><Input type="number" value={f.opening_a} onChange={set("opening_a")} data-testid="product-opening-a-input" /></Field>
            <Field label="Opening WH B"><Input type="number" value={f.opening_b} onChange={set("opening_b")} data-testid="product-opening-b-input" /></Field>
          </div>
          <p className="text-xs text-muted-foreground">Opening stock is the base quantity before all recorded IN/OUT entries.</p>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="product-save-btn">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
        <ConflictDialog open={conflict} what="product" onClose={() => setConflict(false)} onReload={() => { setConflict(false); reload(); }} />
      </DialogContent>
    </Dialog>
  );
};

export const WeightCell = ({ product, editable }) => {
  const [v, setV] = useState(product.weight_kg ?? "");
  useEffect(() => setV(product.weight_kg ?? ""), [product.weight_kg]);
  if (!editable) return <span>{product.weight_kg ?? "—"}</span>;
  const save = async () => {
    const next = v === "" ? null : Number(v);
    if (next === (product.weight_kg ?? null)) return;
    try {
      await api.patch(`/products/${product.id}/weight`, { weight_kg: next });
      product.weight_kg = next;
      toast.success(`Weight saved · ${product.sku}`);
    } catch (err) { toast.error(errMsg(err)); setV(product.weight_kg ?? ""); }
  };
  return (
    <Input type="number" step="any" min="0" value={v} placeholder="kg" onChange={(e) => setV(e.target.value)} onBlur={save}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className={`h-8 w-20 mx-auto text-center font-mono ${v === "" ? "border-amber-400 bg-amber-50" : ""}`} data-testid={`weight-input-${product.sku}`} />
  );
};

export const CountDialog = ({ open, onOpenChange, product }) => {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  useEffect(() => {
    if (open && product) { setA(product.physical_a ?? ""); setB(product.physical_b ?? ""); }
  }, [open, product]);
  const toNum = (v) => (v === "" || v == null ? null : Number(v));
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/products/${product.id}/count`, { physical_a: toNum(a), physical_b: toNum(b) });
      toast.success("Physical count saved");
      notifyStockChanged();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="count-dialog">
        <DialogHeader><DialogTitle>Physical stock count</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <p className="text-sm text-muted-foreground">{product?.name}</p>
          <div className="text-xs font-mono text-muted-foreground">System: WH A {product?.stock_a} · WH B {product?.stock_b}</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Counted in WH A"><Input type="number" value={a} onChange={(e) => setA(e.target.value)} data-testid="count-a-input" /></Field>
            <Field label="Counted in WH B"><Input type="number" value={b} onChange={(e) => setB(e.target.value)} data-testid="count-b-input" /></Field>
          </div>
          <DialogFooter><Button type="submit" data-testid="count-save-btn">Save count</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
